<template>
  <main class="ndfl-certificate">
    <div class="ndfl-certificate__container">
      <button-back />
      <h1 class="ndfl-certificate__title">Справка для налоговой</h1>
      <ndfl-certificate-form
        v-if="isFormLoaded"
        only-view
        v-bind:identification-code-options="identificationCodeOptions"
        v-bind:input-errors="inputErrors"
        v-bind:invoices="invoices"
        v-bind="certificateProps"
      >
        <template v-slot:actions>
          <button
            class="ndfl-certificate__button-download"
            type="button"
            v-on:click="onDownload">Скачать</button>
        </template>
      </ndfl-certificate-form>
    </div>
  </main>
</template>

<script>
import ButtonBack from '@/components/ButtonBack.vue';
import NdflCertificateForm from '@/components/NdflCertificateForm.vue';
import notifierMixin from '@/mixins/notifierMixin';
import saveFileMixin from '@/mixins/saveFileMixin';
import { Maybe } from 'monet';

/**
 * @description Компонент страницы создания справки НДФЛ.
 */
export default {
  components: {
    ButtonBack,
    NdflCertificateForm,
  },
  computed: {
    /**
     * @description Идентификатор справки.
     * @returns {number}
     */
    certificateId() {
      return +this.$route.params.id;
    },
    /**
     * @description Данные справки.
     * @returns {any}
     */
    certificateProps() {
      return this.certificate.fold({})((certificate) => {
        const { guardian, patient } = certificate;
        return {
          initialAmountByExpensive: certificate.amount_by_expensive,
          initialAmountBySimple: certificate.amount_by_simple,
          initialCertificateNumber: certificate.certificate_number,
          initialCorrectionNumber: certificate.correction_number,
          initialGuardianBirthDate: guardian.birth_date,
          initialGuardianIdentificationCode: guardian.identification_code,
          initialGuardianIdentificationSerialAndNumber: guardian.identification_serial_and_number,
          initialGuardianIdentificationWhenIssued: guardian.identification_when_issued,
          initialGuardianInn: guardian.inn,
          initialGuardianName: guardian.name,
          initialGuardianOnlyInn: guardian.is_only_inn,
          initialGuardianPatronymic: guardian.patronymic,
          initialGuardianSurname: guardian.surname,
          initialIsSinglePerson: certificate.is_single_person,
          initialIssuedDate: certificate.issued_date,
          initialPatientBirthDate: patient.birth_date,
          initialPatientIdentificationCode: patient.identification_code,
          initialPatientIdentificationSerialAndNumber: patient.identification_serial_and_number,
          initialPatientIdentificationWhenIssued: patient.identification_when_issued,
          initialPatientInn: patient.inn,
          initialPatientName: patient.name,
          initialPatientOnlyInn: patient.is_only_inn,
          initialPatientPatronymic: patient.patronymic,
          initialPatientSurname: patient.surname,
          initialReportingYear: certificate.reporting_year,
        };
      });
    },
  },
  created() {
    this.fetchCertificate(this.certificateId);
  },
  data() {
    return {
      certificate: Maybe.none(),
      documentName: '',
      identificationCodeOptions: [],
      inputErrors: {},
      invoices: [],
      isAwaitDownload: true,
      isAwaitRequest: false,
      isFormLoaded: false,
    };
  },
  methods: {
    /**
     * @description Получить справку.
     * @param {number} certificateId Идентификатор справки.
     * @returns {void}
     */
    fetchCertificate(certificateId) {
      this.isFormLoaded = false;
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      const onSuccess = ({
        data,
        invoices,
        document_name: documentName,
        identification_code_options: identificationCodeOptions,
      }) => {
        this.certificate = Maybe.fromEmpty(data);
        this.invoices = invoices;
        this.documentName = documentName;
        this.identificationCodeOptions = identificationCodeOptions;
        this.isFormLoaded = true;
      };
      this.$registryNdflCertificateGateway
        .viewById(certificateId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать скачивание справки.
     * @param {number} certificateId Идентификатор справки.
     * @param {string} name          Название файла.
     * @returns {void}
     */
    onDownload() {
      this.isAwaitDownload = true;
      const onFail = (error) => { this.notifyFail(error.title); };
      const onSuccess = (blob) => {
        this.saveFile(blob, this.documentName);
      };
      this
        .$registryNdflCertificateGateway
        .downloadById(this.certificateId)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitDownload = false; });
    },
  },
  mixins: [notifierMixin, saveFileMixin],
  name: 'ClinicPanelNdflCertificate',
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

.ndfl-certificate {
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  &__container {
    width: 1187px;
    margin: 0 auto 0 0;
    padding: 38px 20px 200px 23px;
  }
  &__title {
    color: #000000;
    margin: 0 0 27px 0;
  }
  &__button {
    &-download {
      max-width: 400px;
      @include buttons.btn-blue(100%, 56px, 20px);
      margin: 40px 0 0 0;
      &:disabled {
        background-color: var(--unfocus-color);
      }
    }
  }
}
@media screen and (max-width: 480px) {
  .ndfl-certificate {
    &__container {
      padding: 38px 10px 140px;
    }
  }
}
</style>
